import React, { useEffect, useRef } from 'react'
import './modal.css'

type ModalProps = {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  subTitle?: React.ReactNode | string
  className?: string
  width?: string // New prop for modal width
  closeable?: boolean
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  subTitle,
  className,
  children,
  width = 'auto',
  closeable = true
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      previouslyFocused.current = document.activeElement as HTMLElement
    } else {
      previouslyFocused.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )

    focusable?.[0]?.focus()
  }, [isOpen])

  // Lock background scroll.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <div className={`modal-overlay ${isOpen ? 'show' : ''}`} onClick={onClose}>
      <div
        ref={modalRef}
        className="modal modal-animation show"
        id="modal"
        tabIndex={1}
        aria-labelledby="modalTitle"
        aria-modal="true"
        role="dialog"
        style={{ display: isOpen ? 'block' : 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-dialog modal-dialog-centered bg-transparent"
          role="document"
          style={{ maxWidth: width }}
        >
          <div className={`modal-content bg-white ${className}`}>
            {closeable && (
              <button
                type="button"
                className="btn-close position-absolute top-0 end-0"
                data-dismiss="modal"
                aria-label="Close"
                onClick={onClose}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            )}

            <div className="modal-header border-0 border-none text-center w-full justify-content-center">
              {title && (
                <h6
                  className="h6 fw-bold text-primary text-center mb-0"
                  id="modalExampleTitle"
                >
                  {title}
                </h6>
              )}
            </div>
            <div className="modal-body">
              {subTitle && (
                <p className="text-secondary subtitle text-center font-sm">
                  {subTitle}
                </p>
              )}
              <div>{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
